import React from "react";
import { motion } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import SEOHead from "../components/common/SEOHead";
import { useNavigate } from "react-router-dom";

const Auditions = () => {
  const navigate = useNavigate();

  const seoData = {
    title: "Auditions - Livneu | Join Sri Lanka's Premier VTuber Agency",
    description:
      "Apply to become a VTuber talent with Livneu! Open auditions for virtual streamers, artists, and creators across Sri Lanka and South Asia. Start your virtual entertainment career today.",
    image: "https://livneu.levelstair.com/ShareImage.png",
    url: "https://livneu.levelstair.com/auditions",
    keywords:
      "VTuber auditions, virtual streamer applications, Sri Lanka VTuber, South Asia virtual talent, Livneu auditions, virtual entertainment careers",
  };

  const requirements = [
    {
      category: "Main Requirements",
      items: [
        "Must be aged 18 or older at the time of application",
        "Able to stream for at least 3 times per week (minimum 2 hours per stream)",
        "Strong communication skills and ability to engage with audiences",
        "Basic understanding of streaming platforms and social media",
        "Commitment to maintaining professional standards and brand guidelines"
      ]
    },
    {
      category: "Other Requirements",
      items: [
        "Able to commit to ongoing activities and content creation",
        "Not currently under exclusive contract with another talent agency",
        "If currently affiliated with an agency, must be able to terminate contract at own discretion",
        "Willing to comply with Livneu's guidelines and code of conduct",
        "Access to reliable internet connection and basic streaming equipment",
        "Respect for cultural diversity and inclusive community values"
      ]
    }
  ];

  const processSteps = [
    {
      step: "01",
      title: "Application Review",
      description: "Our team carefully reviews your application, portfolio, and submitted materials."
    },
    {
      step: "02", 
      title: "Initial Interview",
      description: "Selected candidates participate in online interviews to assess personality, goals, and fit."
    },
    {
      step: "03",
      title: "Talent Assessment", 
      description: "Practical evaluation of your streaming abilities, creativity, and audience engagement skills."
    },
    {
      step: "04",
      title: "Final Review",
      description: "Final decision and contract negotiations for successful candidates."
    }
  ];

  return (
    <HelmetProvider>
      <div className="min-h-screen">
        <SEOHead seoData={seoData} />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-gradient-auditions">
          {/* Kawaii Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-pastel-purple/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-pastel-blue/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "2s" }}></div>
            <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-kawaii-mint/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: "4s" }}></div>
          </div>

          <div className="container mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-5xl md:text-7xl font-title text-text-accent mb-6 leading-tight kawaii-glow">
                Livneu
                <span className="block text-text-primary">
                  Auditions Open
                </span>
              </h1>
              <motion.p
                className="text-xl md:text-2xl text-text-secondary mb-8 leading-relaxed font-body"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Join Sri Lanka's premier VTuber agency and become part of the next generation of virtual entertainers. 
                We're looking for passionate creators ready to make their mark in the digital entertainment world.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* What is Livneu Audition */}
        <section className="py-20 px-6 bg-bg-primary">
          <div className="container mx-auto max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-title text-text-primary mb-8">
                What is Livneu Audition?
              </h2>
              <div className="glass-card backdrop-blur-sm rounded-2xl p-8 md:p-12 border-pastel-purple/20">
                <p className="text-lg md:text-xl leading-relaxed text-text-secondary mb-6 font-body">
                  Livneu hosts auditions to discover exceptional talents with diverse skills and unique personalities 
                  from across Sri Lanka and South Asia. Our mission is to nurture and develop the next generation of virtual 
                  entertainment stars.
                </p>
                <p className="text-lg md:text-xl leading-relaxed text-text-secondary font-body">
                  Whether you're an aspiring streamer, talented artist, or creative professional, we provide comprehensive 
                  support including avatar design, technical training, content strategy, and global audience development.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Requirements Section */}
        <section className="py-20 px-6 bg-bg-secondary">
          <div className="container mx-auto max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-title text-text-primary mb-8">
                Application Requirements
              </h2>
              <p className="text-xl text-text-secondary max-w-3xl mx-auto font-body">
                We welcome applications from passionate creators who meet our basic requirements and share our vision.
              </p>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-8">
              {requirements.map((section, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="glass-card backdrop-blur-sm rounded-2xl p-8 border-pastel-purple/20"
                >
                  <h3 className="text-2xl font-accent text-text-accent mb-6">
                    {section.category}
                  </h3>
                  <ul className="space-y-4">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-pastel-purple rounded-full mt-3 flex-shrink-0"></div>
                        <span className="text-text-secondary leading-relaxed font-body">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Application Process */}
        <section className="py-20 px-6">
          <div className="container mx-auto max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-8">
                Application Process
              </h2>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto">
                Our streamlined process ensures we find the best talent while providing a fair and transparent experience.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {processSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700 text-center"
                >
                  <div className="text-4xl font-bold text-purple-400 mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-4">
                    {step.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {step.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Apply Now CTA */}
        <section className="py-20 px-6 bg-gradient-to-r from-purple-400/10 to-blue-500/10">
          <div className="container mx-auto max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-8">
                Ready to Start Your Journey?
              </h2>
              <p className="text-xl text-gray-700 mb-12 max-w-2xl mx-auto">
                Take the first step towards becoming a virtual entertainment star. 
                Join Livneu and let's create something amazing together.
              </p>
              <motion.button
                onClick={() => navigate('/apply')}
                className="bg-gradient-to-r from-purple-400 to-blue-500 text-black px-12 py-4 rounded-full text-xl font-bold hover:from-purple-300 hover:to-blue-400 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Apply Now
              </motion.button>
            </motion.div>
          </div>
        </section>
      </div>
    </HelmetProvider>
  );
};

export default Auditions;
